import React, {
  TextareaHTMLAttributes,
  useCallback,
  useRef,
} from 'react';
import baseStyles from '../form-field-base.module.css';
import { useAutoResizeTextarea } from '../../../hooks/useAutoResizeTextarea';
import styles from './FormTextarea.module.css';

interface FormTextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  labelAction?: React.ReactNode;
  error?: string;
  required?: boolean;
  containerClassName?: string;
  autoResize?: boolean;
  minRows?: number;
  maxRows?: number;
}

const FormTextarea: React.FC<FormTextareaProps> = ({
  label,
  labelAction,
  error,
  required: isRequired,
  id,
  containerClassName = '',
  className = '',
  readOnly,
  autoResize = true,
  minRows,
  maxRows,
  rows,
  value,
  onChange,
  ...rest
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const resolvedMinRows = minRows ?? rows ?? 1;
  // Em leitura, cresce com o conteúdo; em edição, limita a ~12 linhas.
  const resolvedMaxRows = maxRows ?? (readOnly ? Number.POSITIVE_INFINITY : 12);

  const adjustHeight = useAutoResizeTextarea(textareaRef, {
    enabled: autoResize,
    minRows: resolvedMinRows,
    maxRows: resolvedMaxRows,
    value: typeof value === 'string' ? value : undefined,
  });

  const handleChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange?.(event);
    if (autoResize) {
      requestAnimationFrame(() => adjustHeight());
    }
  }, [adjustHeight, autoResize, onChange]);

  const textareaCls = [
    styles.textarea,
    autoResize ? styles.textareaAutoResize : '',
    readOnly ? styles.textareaReadOnly : '',
    error ? styles.textareaError : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={`${baseStyles.fieldGroup} ${containerClassName}`}>
      {label && (
        <div className={baseStyles.labelRow}>
          <label htmlFor={id} className={baseStyles.label}>
            {label}
            {isRequired && <span className={baseStyles.required}>*</span>}
          </label>
          {labelAction}
        </div>
      )}
      <textarea
        ref={textareaRef}
        id={id}
        className={textareaCls}
        readOnly={readOnly}
        tabIndex={readOnly ? -1 : undefined}
        autoComplete="off"
        value={value}
        onChange={handleChange}
        rows={autoResize ? resolvedMinRows : rows}
        {...rest}
      />
      {error && <span className={baseStyles.errorText}>{error}</span>}
    </div>
  );
};

export default FormTextarea;
